import { Alert, Button, Card, Col, Form, Input, Row, Select, Space, Table, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import {
  EducationGradeSelect,
  EducationSessionSelect,
} from '@/pages/education/components/EducationLookupSelects';

type RouteRow = {
  id: string;
  code: string;
  name: string;
  is_active?: number | boolean;
  capacity?: number | null;
  active_riders?: number;
};
type RouteDetail = {
  route: { capacity?: number | null };
  stops: Array<{ id: string; code: string; name: string }>;
  active_riders?: number;
};
type EligibleStudent = {
  student_id: string;
  admission_no?: string;
  first_name?: string;
  last_name?: string;
  grade_code?: string;
  grade_name?: string;
};

type BulkForm = {
  session_id: string;
  route_id: string;
  stop_id: string;
  effective_from: string;
  grade_level_id?: string;
  q?: string;
  notes?: string;
};

function asArray<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === 'object' && Array.isArray((payload as { data?: unknown }).data)) {
    return (payload as { data: T[] }).data;
  }
  return [];
}

export default function TransportEnrollmentBulkPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.transport.manage') ||
      user?.permissions?.includes('education.admin')
  );
  const [form] = Form.useForm<BulkForm>();
  const sessionId = Form.useWatch('session_id', form);
  const routeId = Form.useWatch('route_id', form);
  const gradeId = Form.useWatch('grade_level_id', form);
  const q = Form.useWatch('q', form);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  useEffect(() => {
    setSelectedIds([]);
  }, [sessionId, gradeId]);

  const routes = useQuery({
    queryKey: ['education-transport-routes'],
    queryFn: async () => asArray<RouteRow>((await api.get('/education/transport/routes')).data),
    enabled: canManage,
  });
  const routeDetail = useQuery({
    queryKey: ['education-transport-route', routeId],
    enabled: Boolean(routeId),
    queryFn: () => api.get<RouteDetail>(`/education/transport/routes/${routeId}`),
  });
  const eligible = useQuery({
    queryKey: ['education-transport-eligible', sessionId, gradeId, q],
    enabled: Boolean(sessionId),
    queryFn: async () =>
      asArray<EligibleStudent>(
        (
          await api.get('/education/transport/enrollments/eligible', {
            params: {
              session_id: sessionId,
              grade_level_id: gradeId || undefined,
              q: q || undefined,
            },
          })
        ).data
      ),
  });

  const bulk = useMutation({
    mutationFn: (values: BulkForm & { student_ids: string[] }) =>
      api.post('/education/transport/enrollments/bulk', {
        session_id: values.session_id,
        route_id: values.route_id,
        stop_id: values.stop_id,
        effective_from: values.effective_from,
        notes: values.notes || null,
        student_ids: values.student_ids,
      }),
    onSuccess: (result) => {
      const body = result.data as {
        succeeded?: number;
        failed?: number;
        waitlisted?: number;
        attempted?: number;
      };
      message.success(
        t('transportBulkEnrollmentDone', {
          succeeded: body?.succeeded ?? 0,
          failed: body?.failed ?? 0,
          waitlisted: body?.waitlisted ?? 0,
          attempted: body?.attempted ?? 0,
        })
      );
      setSelectedIds([]);
      queryClient.invalidateQueries({ queryKey: ['education-transport-enrollments'] });
      queryClient.invalidateQueries({ queryKey: ['education-transport-eligible'] });
      queryClient.invalidateQueries({ queryKey: ['education-transport-routes'] });
      navigate('/education/transport/enrollments');
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const activeRoutes = useMemo(
    () => (routes.data ?? []).filter((r) => !(r.is_active === 0 || r.is_active === false)),
    [routes.data]
  );
  const stopOptions = useMemo(
    () =>
      (routeDetail.data?.data?.stops ?? []).map((s) => ({
        value: s.id,
        label: `${s.code} — ${s.name}`,
      })),
    [routeDetail.data]
  );
  const rows = eligible.data ?? [];
  const capacity = routeDetail.data?.data?.route?.capacity;
  const riders = routeDetail.data?.data?.active_riders ?? 0;
  const lookupError = routes.isError || routeDetail.isError;

  if (!canManage) {
    return (
      <EducationPageChrome title={t('bulkTransportEnrollment')} description={t('bulkTransportEnrollmentIntro')}>
        <Alert type="warning" showIcon message={t('unavailable', { status: 403 })} />
      </EducationPageChrome>
    );
  }

  return (
    <EducationPageChrome
      title={t('bulkTransportEnrollment')}
      description={t('bulkTransportEnrollmentIntro')}
      extra={
        <Button onClick={() => navigate('/education/transport/enrollments')}>
          {t('backToTransportEnrollments')}
        </Button>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {lookupError ? (
          <Alert
            type="error"
            showIcon
            message={t('transportLookupFailed', {
              defaultValue: 'Could not load transport routes or stops. Refresh or re-login after permissions update.',
            })}
          />
        ) : null}

        <Card size="small">
          <Form
            form={form}
            layout="vertical"
            initialValues={{ effective_from: new Date().toISOString().slice(0, 10) }}
            onValuesChange={(changed) => {
              if ('route_id' in changed) {
                form.setFieldValue('stop_id', undefined);
              }
            }}
            onFinish={(values) => {
              if (!selectedIds.length) {
                message.warning(t('transportBulkSelectStudents'));
                return;
              }
              bulk.mutate({ ...values, student_ids: selectedIds });
            }}
          >
            <Row gutter={[16, 8]}>
              <Col xs={24} md={8} lg={6}>
                <Form.Item name="session_id" label={t('session')} rules={[{ required: true }]}>
                  <EducationSessionSelect style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8} lg={6}>
                <Form.Item name="grade_level_id" label={t('grade')}>
                  <EducationGradeSelect allowClear style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8} lg={6}>
                <Form.Item name="q" label={t('search')}>
                  <Input allowClear placeholder={t('feeOutstandingSearchHint')} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8} lg={6}>
                <Form.Item name="route_id" label={t('transportRoute')} rules={[{ required: true }]}>
                  <Select
                    style={{ width: '100%' }}
                    loading={routes.isLoading}
                    notFoundContent={
                      routes.isError
                        ? t('unavailable', { status: '' })
                        : t('transportNoRoutes', { defaultValue: 'No active routes' })
                    }
                    options={activeRoutes.map((r) => ({
                      value: r.id,
                      label: `${r.code} — ${r.name}${
                        r.capacity != null ? ` (${r.active_riders ?? 0}/${r.capacity})` : ''
                      }`,
                    }))}
                    showSearch
                    optionFilterProp="label"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8} lg={6}>
                <Form.Item name="stop_id" label={t('stop')} rules={[{ required: true }]}>
                  <Select
                    style={{ width: '100%' }}
                    options={stopOptions}
                    showSearch
                    optionFilterProp="label"
                    disabled={!routeId}
                    loading={Boolean(routeId) && routeDetail.isLoading}
                    notFoundContent={
                      routeId
                        ? t('transportNoStops', { defaultValue: 'No stops on this route' })
                        : t('transportPickRouteFirst', { defaultValue: 'Select a route first' })
                    }
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8} lg={6}>
                <Form.Item name="effective_from" label={t('effectiveOn')} rules={[{ required: true }]}>
                  <Input placeholder="YYYY-MM-DD" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="notes" label={t('notes')}>
              <Input.TextArea rows={2} style={{ maxWidth: 640 }} />
            </Form.Item>
            {capacity != null ? (
              <Alert
                type={Number(riders) + selectedIds.length > Number(capacity) ? 'warning' : 'info'}
                showIcon
                style={{ marginBottom: 16 }}
                message={t('transportBulkCapacityHint', {
                  riders,
                  capacity: Number(capacity),
                  selected: selectedIds.length,
                })}
              />
            ) : null}
            <Space>
              <Button type="primary" htmlType="submit" loading={bulk.isPending} disabled={!selectedIds.length}>
                {t('enrollSelected', { count: selectedIds.length })}
              </Button>
              <Button onClick={() => setSelectedIds(rows.map((r) => r.student_id))} disabled={!rows.length}>
                {t('selectAllVisible')}
              </Button>
              <Button onClick={() => setSelectedIds([])} disabled={!selectedIds.length}>
                {t('clearSelection')}
              </Button>
            </Space>
          </Form>
        </Card>

        <Card size="small" title={t('eligibleStudents')}>
          {!sessionId ? (
            <Alert type="info" showIcon message={t('transportBulkPickSession')} />
          ) : eligible.isError ? (
            <Alert
              type="error"
              showIcon
              message={t('transportEligibleFailed', {
                defaultValue: 'Could not load eligible students for this session.',
              })}
            />
          ) : (
            <Table
              rowKey="student_id"
              size="small"
              loading={eligible.isLoading || eligible.isFetching}
              dataSource={rows}
              pagination={{ pageSize: 50 }}
              locale={{
                emptyText: t('transportEligibleEmpty', {
                  defaultValue: 'No eligible students — all academically enrolled students already have transport.',
                }),
              }}
              rowSelection={{
                selectedRowKeys: selectedIds,
                onChange: (keys) => setSelectedIds(keys.map(String)),
                preserveSelectedRowKeys: true,
              }}
              columns={[
                {
                  title: t('student'),
                  render: (_: unknown, row: EligibleStudent) =>
                    `${row.admission_no || ''} — ${[row.first_name, row.last_name].filter(Boolean).join(' ')}`,
                },
                {
                  title: t('grade'),
                  render: (_: unknown, row: EligibleStudent) =>
                    row.grade_name || row.grade_code || t('allGrades'),
                },
              ]}
            />
          )}
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
